import { apiRequest } from "./apiClient";
import { normalizeCategoryId } from "./categoryService";
import { stripHtml } from "../utils/richText";

const productImages = {
  cell: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?auto=format&fit=crop&w=900&q=80",
  inverter: "https://images.unsplash.com/photo-1562408590-e32931084e23?auto=format&fit=crop&w=900&q=80",
  "bms-smart": "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=80",
  "battery-pack": "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?auto=format&fit=crop&w=900&q=80",
  charger: "https://images.unsplash.com/photo-1593941707874-ef25b8b4a92b?auto=format&fit=crop&w=900&q=80",
  machine: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=900&q=80",
  connector: "https://images.unsplash.com/photo-1606813907291-d86efa9b94db?auto=format&fit=crop&w=900&q=80",
  wire: "https://images.unsplash.com/photo-1606813907291-d86efa9b94db?auto=format&fit=crop&w=900&q=80",
  safety: "https://images.unsplash.com/photo-1603398938378-e54eab446dde?auto=format&fit=crop&w=900&q=80",
  accessories: "https://images.unsplash.com/photo-1518779578993-ec3579fee39f?auto=format&fit=crop&w=900&q=80",
};

const categoryNames = {
  cell: "Cell",
  inverter: "Inverter",
  "bms-smart": "BMS Smart",
  "battery-pack": "Battery Pack",
  charger: "Charger",
  machine: "Machines",
  connector: "Connectors",
  wire: "Wires & Strips",
  safety: "Safety",
  accessories: "Accessories",
  scooty: "EvScooty",
  scooters1: "EvScooty",
};

const scootyColorOptions = [
  { name: "Pearl White", hex: "#f8fafc" },
  { name: "Matte Black", hex: "#111827" },
  { name: "Racing Red", hex: "#dc2626" },
  { name: "Ocean Blue", hex: "#2563eb" },
  { name: "Mint Green", hex: "#22c55e" },
];

const isScootyProduct = (product, category) => {
  return category === "scooters1";
};

export const normalizeProduct = (product) => {
  const category = normalizeCategoryId(product.category);
  const listOrFallback = (items, fallback) => {
    const cleaned = Array.isArray(items) ? items.map((item) => stripHtml(item)).filter(Boolean) : [];
    return cleaned.length ? cleaned : fallback;
  };
  const variants = Array.isArray(product.variants)
    ? product.variants
        .map((variant) => ({
          label: String(variant.label || "").trim(),
          unit: variant.unit || product.unit || "pcs",
          minQty: Number(variant.minQty || product.minQty || 1),
          stock: Number(variant.stock ?? product.stock ?? 0),
          price: Number(variant.price || 0),
        }))
        .filter((variant) => variant.label)
    : [];
  const spec = stripHtml(product.spec || product.shortDescription || "EV battery product for B2B supply.");
  const specifications = listOrFallback(product.specifications, [spec, `${product.stock || 0} pcs available`, `Minimum quantity ${product.minQty || 1}`]);
  const image = product.image || productImages[category] || productImages.accessories;
  const images = Array.isArray(product.images) && product.images.length ? product.images : [image];
  const price = Number(product.price || 0);
  const discountType = product.discountType || "none";
  const discountValue = Number(product.discountValue || 0);
  const discountAmount = product.discountAmount !== undefined
    ? Number(product.discountAmount || 0)
    : discountType === "percent"
      ? Math.round(price * Math.min(discountValue, 100) / 100)
      : discountType === "amount"
        ? Math.min(discountValue, price)
        : 0;
  const finalPrice = Number(product.finalPrice ?? Math.max(0, price - discountAmount));
  const gstPercent = Number(product.gstPercent || 0);
  const gstAmount = Number(product.gstAmount ?? Math.round(finalPrice * Math.max(0, gstPercent) / 100));
  const priceWithGst = Number(product.priceWithGst ?? finalPrice + gstAmount);
  const colorOptions = Array.isArray(product.colorOptions) && product.colorOptions.length
    ? product.colorOptions
    : isScootyProduct(product, category)
      ? scootyColorOptions
      : [];

  return {
    ...product,
    category,
    name: stripHtml(product.name),
    price,
    rating: Number(product.rating || 4.5),
    discountType,
    discountValue,
    discountAmount,
    finalPrice,
    gstPercent,
    gstAmount,
    priceWithGst,
    hasDiscount: discountAmount > 0,
    discountLabel: product.discountLabel || (discountType === "percent" && discountValue ? `${discountValue}% off` : discountType === "amount" && discountValue ? `₹${discountValue} off` : ""),
    brand: stripHtml(product.brand || product.badge || "Mozo EV"),
    categoryName: stripHtml(product.categoryName || categoryNames[category] || category),
    unit: product.unit || "pcs",
    optionGroupName: stripHtml(product.optionGroupName || "Size"),
    variants,
    image,
    images,
    colorOptions,
    shortDescription: stripHtml(product.shortDescription || spec),
    specifications,
    moq: product.moq || `${product.minQty || 1} ${product.unit || "pcs"}`,
    minQty: Number(product.minQty || 1),
    sizes: listOrFallback(product.sizes, variants.length ? variants.map((variant) => variant.label) : ["Standard", "Bulk supply", "Custom requirement"]),
    packaging: stripHtml(product.packaging || "Secure carton or bulk packaging"),
    applications: listOrFallback(product.applications, ["EV battery packs", "Repair workshops", "Dealer supply"]),
    features: listOrFallback(product.features, [product.badge || "Ready supply", "Specification verified", "Quote-ready product"]),
    benefits: listOrFallback(product.benefits, ["Fast sourcing", "Bulk quotation support", "Reliable procurement"]),
  };
};

const splitCategories = (category = "all") => {
  if (Array.isArray(category)) return category.map((item) => String(item || "").trim()).filter(Boolean);
  return String(category || "all")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const sortProducts = (products, sort = "featured") => {
  const sorted = [...products];
  if (sort === "low") sorted.sort((a, b) => a.price - b.price);
  if (sort === "high") sorted.sort((a, b) => b.price - a.price);
  if (sort === "featured") sorted.sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));
  if (sort === "stock") sorted.sort((a, b) => b.stock - a.stock);
  return sorted;
};

const buildProductQuery = ({ query = "", category = "all", sort = "featured" } = {}) => {
  const params = new URLSearchParams();
  params.set("limit", "100");
  if (query.trim()) params.set("q", query.trim());
  if (category && category !== "all") params.set("category", normalizeCategoryId(category));
  if (sort) params.set("sort", sort);
  const qs = params.toString();
  return qs ? `/products?${qs}` : "/products";
};

export const getProducts = async (options = {}) => {
  const categoryList = splitCategories(options.category).filter((category) => category !== "all");

  if (categoryList.length > 1) {
    const responses = await Promise.all(
      categoryList.map((category) => apiRequest(buildProductQuery({ ...options, category }))),
    );
    const merged = new Map();
    responses.forEach((data) => {
      (data.products || []).map(normalizeProduct).forEach((product) => {
        merged.set(product.id, product);
      });
    });
    return sortProducts([...merged.values()], options.sort || "featured");
  }

  const data = await apiRequest(buildProductQuery({ ...options, category: categoryList[0] || options.category }));
  return (data.products || []).map(normalizeProduct);
};

export const getProductById = async (id) => {
  const data = await apiRequest(`/products/${id}`);
  return normalizeProduct(data.product);
};

export const getProductsByCategory = async (categoryId) => getProducts({ category: categoryId });
export const getFeaturedProducts = async () => {
  const products = await getProducts({ sort: "featured" });
  return products;
};
export const searchProducts = getProducts;
